import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import stateData from '../data/stateData';

const { FiArrowLeft, FiFileText, FiScale, FiDollarSign, FiCalendar, FiUsers, FiBookOpen, FiAlertCircle, FiExternalLink, FiPhone, FiMapPin, FiInfo, FiCheckCircle } = FiIcons;

const StateDetail = () => {
  const { state } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  const stateInfo = stateData[state];

  if (!stateInfo) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto text-center">
          <SafeIcon icon={FiAlertCircle} className="text-6xl text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">State Information Not Available</h1>
          <p className="text-gray-600 mb-6">
            Detailed information for this state is currently being compiled. Please check back soon.
          </p>
          <Link
            to="/states"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to States
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiBookOpen },
    { id: 'legal-requirements', label: 'Legal Requirements', icon: FiScale },
    { id: 'custody-types', label: 'Custody Types', icon: FiUsers },
    { id: 'parenting-plans', label: 'Parenting Plans', icon: FiFileText },
    { id: 'child-support', label: 'Child Support', icon: FiDollarSign },
    { id: 'resources', label: 'Resources', icon: FiCalendar }
  ];

  const ResourceLink = ({ resource }) => {
    if (typeof resource === 'string') {
      return <li>• {resource}</li>;
    }

    return (
      <li className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h5 className="font-semibold text-gray-900 mb-1">{resource.name}</h5>
            <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
            <div className="flex items-center space-x-4 text-sm">
              {resource.url && (
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary-600 hover:text-primary-700"
                >
                  <SafeIcon icon={FiExternalLink} className="mr-1" />
                  Visit Website
                </a>
              )}
              {resource.phone && (
                <a
                  href={`tel:${resource.phone}`}
                  className="inline-flex items-center text-secondary-600 hover:text-secondary-700"
                >
                  <SafeIcon icon={FiPhone} className="mr-1" />
                  {resource.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </li>
    );
  };

  const getTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Family Law Overview for {stateInfo.name}</h3>
            <div className="prose max-w-none">
              <p className="text-gray-600 leading-relaxed mb-6">
                {stateInfo.name} has distinct family law characteristics that fathers should understand when navigating custody, support, and parenting matters.
              </p>
              <h4 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Key Points for Fathers:</h4>
              <ul className="space-y-2 text-gray-600 mb-6">
                {stateInfo.overview.keyPoints.map((point, index) => (
                  <li key={index}>• {point}</li>
                ))}
              </ul>
              <h4 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Unique Features of {stateInfo.name}:</h4>
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <ul className="space-y-2 text-primary-800">
                  {stateInfo.overview.uniqueFeatures.map((feature, index) => (
                    <li key={index}>• {feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'legal-requirements':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Legal Requirements in {stateInfo.name}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-primary-900 mb-3">Residency Requirements</h4>
                <p className="text-primary-800">{stateInfo.legalRequirements.residency}</p>
              </div>
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-secondary-900 mb-3">Filing Requirements</h4>
                <p className="text-secondary-800">{stateInfo.legalRequirements.filing}</p>
              </div>
              <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-accent-900 mb-3">Paternity Establishment</h4>
                <p className="text-accent-800">{stateInfo.legalRequirements.paternity}</p>
              </div>
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-primary-900 mb-3">Documentation</h4>
                <p className="text-primary-800">{stateInfo.legalRequirements.documentation}</p>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Special Rules for {stateInfo.name}</h4>
              <ul className="space-y-2 text-gray-700">
                {stateInfo.legalRequirements.uniqueRules.map((rule, index) => (
                  <li key={index}>• {rule}</li>
                ))}
              </ul>
            </div>

            {/* Filing Information for Nebraska */}
            {state === 'nebraska' && stateInfo.resources.filingInformation && (
              <div className="mt-8">
                <h4 className="text-xl font-bold text-gray-900 mb-6">Filing Information</h4>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h5 className="text-lg font-semibold text-gray-900 mb-4">Court Venues</h5>
                    <div className="space-y-4">
                      {stateInfo.resources.filingInformation.venues.map((venue, index) => (
                        <div key={index} className="border-l-4 border-primary-500 pl-4">
                          <h6 className="font-medium text-gray-900">{venue.name}</h6>
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <SafeIcon icon={FiMapPin} className="mr-1" />
                            {venue.address}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center mt-1">
                            <SafeIcon icon={FiPhone} className="mr-1" />
                            {venue.phone}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{venue.jurisdiction}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h5 className="text-lg font-semibold text-gray-900 mb-4">Filing Fees</h5>
                    <div className="space-y-2">
                      {Object.entries(stateInfo.resources.filingInformation.filingFees).map(([type, fee]) => (
                        <div key={type} className="flex justify-between">
                          <span className="capitalize text-gray-700">{type.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="font-medium text-gray-900">{fee}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6">
                      <h6 className="font-medium text-gray-900 mb-2">Required Forms</h6>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {stateInfo.resources.filingInformation.requiredForms.map((form, index) => (
                          <li key={index}>• {form}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {stateInfo.resources.statutes && (
                  <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
                    <h5 className="text-lg font-semibold text-accent-900 mb-4">Relevant Statutes</h5>
                    <div className="space-y-3">
                      {stateInfo.resources.statutes.map((statute, index) => (
                        <div key={index} className="flex items-start justify-between">
                          <div className="flex-1">
                            <h6 className="font-medium text-accent-900">{statute.name}</h6>
                            <p className="text-sm text-accent-700 mb-1">{statute.description}</p>
                          </div>
                          <a
                            href={statute.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-4 inline-flex items-center text-accent-600 hover:text-accent-700 text-sm"
                          >
                            <SafeIcon icon={FiExternalLink} className="mr-1" />
                            View
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'custody-types':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Types of Custody in {stateInfo.name}</h3>
            <div className="space-y-4">
              <div className="border border-primary-200 rounded-lg p-6 bg-white">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Physical Custody</h4>
                <p className="text-gray-600">{stateInfo.custodyTypes.physicalCustody}</p>
              </div>
              <div className="border border-secondary-200 rounded-lg p-6 bg-white">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Legal Custody</h4>
                <p className="text-gray-600">{stateInfo.custodyTypes.legalCustody}</p>
              </div>
              <div className="border border-accent-200 rounded-lg p-6 bg-white">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Joint Custody</h4>
                <p className="text-gray-600">{stateInfo.custodyTypes.jointCustody}</p>
              </div>
              <div className="border border-primary-200 rounded-lg p-6 bg-white">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Visitation Rights</h4>
                <p className="text-gray-600">{stateInfo.custodyTypes.visitation}</p>
              </div>
            </div>
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-accent-900 mb-3">Special Provisions in {stateInfo.name}</h4>
              <ul className="space-y-2 text-accent-800">
                {stateInfo.custodyTypes.specialProvisions.map((provision, index) => (
                  <li key={index}>• {provision}</li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'parenting-plans':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Parenting Plans in {stateInfo.name}</h3>
            <div className="mb-4">
              <div className={`p-4 rounded-lg ${stateInfo.parentingPlans.required ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                <p className={`font-semibold ${stateInfo.parentingPlans.required ? 'text-red-900' : 'text-green-900'}`}>
                  Parenting Plans are {stateInfo.parentingPlans.required ? 'REQUIRED' : 'OPTIONAL'} in {stateInfo.name}
                </p>
              </div>
            </div>
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-primary-900 mb-3">Required Elements</h4>
              <ul className="space-y-2 text-primary-800">
                {stateInfo.parentingPlans.elements.map((element, index) => (
                  <li key={index}>• {element}</li>
                ))}
              </ul>
            </div>
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-secondary-900 mb-3">Tips for Fathers in {stateInfo.name}</h4>
              <ul className="space-y-2 text-secondary-800">
                {stateInfo.parentingPlans.tips.map((tip, index) => (
                  <li key={index}>• {tip}</li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'child-support':
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900">Child Support Guidelines for {stateInfo.name}</h3>
            <p className="text-gray-600">{stateInfo.childSupport.guidelines}</p>
            
            {/* Enhanced Child Support Information for Nebraska */}
            {state === 'nebraska' && stateInfo.childSupport.detailedInfo && (
              <div className="space-y-8">
                {/* Basic Calculation Process */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-primary-900 mb-4">How Child Support is Calculated</h4>
                  <p className="text-primary-800 mb-4">{stateInfo.childSupport.detailedInfo.basicCalculation.description}</p>
                  <div className="bg-white rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-3">Calculation Steps:</h5>
                    <ol className="space-y-2 text-sm text-gray-700">
                      {stateInfo.childSupport.detailedInfo.basicCalculation.steps.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <span className="bg-primary-100 text-primary-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-medium mr-3 mt-0.5">{index + 1}</span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>

                {/* Income Types */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-green-900 mb-4 flex items-center">
                      <SafeIcon icon={FiCheckCircle} className="mr-2" />
                      Income Included
                    </h4>
                    <ul className="space-y-2 text-sm text-green-800">
                      {stateInfo.childSupport.detailedInfo.incomeTypes.included.map((income, index) => (
                        <li key={index}>• {income}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h4 className="text-lg font-semibold text-red-900 mb-4 flex items-center">
                      <SafeIcon icon={FiAlertCircle} className="mr-2" />
                      Income Excluded
                    </h4>
                    <ul className="space-y-2 text-sm text-red-800">
                      {stateInfo.childSupport.detailedInfo.incomeTypes.excluded.map((income, index) => (
                        <li key={index}>• {income}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Deductions */}
                <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-secondary-900 mb-4">Allowable Deductions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="font-medium text-secondary-900 mb-3">Mandatory Deductions:</h5>
                      <ul className="space-y-1 text-sm text-secondary-800">
                        {stateInfo.childSupport.detailedInfo.deductions.mandatory.map((deduction, index) => (
                          <li key={index}>• {deduction}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-secondary-900 mb-3">Additional Considerations:</h5>
                      <ul className="space-y-1 text-sm text-secondary-800">
                        {stateInfo.childSupport.detailedInfo.deductions.optional.map((deduction, index) => (
                          <li key={index}>• {deduction}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Parenting Time Adjustment */}
                <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-accent-900 mb-4">Parenting Time Adjustments</h4>
                  <p className="text-accent-800 mb-4">{stateInfo.childSupport.detailedInfo.parentingTimeAdjustment.description}</p>
                  <div className="bg-white rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-3">Adjustment Thresholds:</h5>
                    <ul className="space-y-2 text-sm text-gray-700">
                      {stateInfo.childSupport.detailedInfo.parentingTimeAdjustment.thresholds.map((threshold, index) => (
                        <li key={index}>• {threshold}</li>
                      ))}
                    </ul>
                    <p className="text-sm text-gray-600 mt-3 italic">{stateInfo.childSupport.detailedInfo.parentingTimeAdjustment.calculation}</p>
                  </div>
                </div>

                {/* Additional Expenses */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-6">Additional Expenses</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Health Insurance */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-medium text-gray-900 mb-2">Health Insurance</h5>
                      <p className="text-sm text-gray-600 mb-2">{stateInfo.childSupport.detailedInfo.additionalExpenses.healthInsurance.description}</p>
                      <p className="text-xs text-gray-500"><strong>Allocation:</strong> {stateInfo.childSupport.detailedInfo.additionalExpenses.healthInsurance.allocation}</p>
                    </div>

                    {/* Childcare */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-medium text-gray-900 mb-2">Childcare</h5>
                      <p className="text-sm text-gray-600 mb-2">{stateInfo.childSupport.detailedInfo.additionalExpenses.childcare.description}</p>
                      <p className="text-xs text-gray-500"><strong>Allocation:</strong> {stateInfo.childSupport.detailedInfo.additionalExpenses.childcare.allocation}</p>
                    </div>

                    {/* Extraordinary Medical */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-medium text-gray-900 mb-2">Extraordinary Medical</h5>
                      <p className="text-sm text-gray-600 mb-2">{stateInfo.childSupport.detailedInfo.additionalExpenses.extraordinaryMedical.description}</p>
                      <div className="text-xs text-gray-500">
                        <p><strong>Examples:</strong></p>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          {stateInfo.childSupport.detailedInfo.additionalExpenses.extraordinaryMedical.examples.slice(0, 3).map((example, index) => (
                            <li key={index}>{example}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Extracurricular */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-medium text-gray-900 mb-2">Extracurricular Activities</h5>
                      <p className="text-sm text-gray-600 mb-2">{stateInfo.childSupport.detailedInfo.additionalExpenses.extracurricular.description}</p>
                      <p className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded">{stateInfo.childSupport.detailedInfo.additionalExpenses.extracurricular.note}</p>
                    </div>
                  </div>
                </div>

                {/* Modifications */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Modification Process</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h5 className="font-medium text-blue-900 mb-3">Grounds for Modification:</h5>
                      <ul className="space-y-1 text-sm text-blue-800">
                        {stateInfo.childSupport.detailedInfo.modifications.grounds.map((ground, index) => (
                          <li key={index}>• {ground}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-blue-900 mb-3">Process Steps:</h5>
                      <ol className="space-y-1 text-sm text-blue-800">
                        {stateInfo.childSupport.detailedInfo.modifications.process.map((step, index) => (
                          <li key={index}>{index + 1}. {step}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-100 rounded">
                    <p className="text-sm text-blue-800"><strong>Important:</strong> {stateInfo.childSupport.detailedInfo.modifications.timeline}</p>
                  </div>
                </div>

                {/* Enforcement */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-red-900 mb-4">Enforcement Methods</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {stateInfo.childSupport.detailedInfo.enforcement.methods.map((method, index) => (
                      <div key={index} className="bg-white rounded p-3 border border-red-200">
                        <p className="text-sm text-red-800">• {method}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    <p className="text-sm text-red-800"><strong>Interest Rate:</strong> {stateInfo.childSupport.detailedInfo.enforcement.interest}</p>
                    <p className="text-sm text-red-800"><strong>Collection:</strong> {stateInfo.childSupport.detailedInfo.enforcement.statute}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Standard Child Support Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-primary-900 mb-3">Factors Considered</h4>
                <ul className="space-y-1 text-primary-800">
                  {stateInfo.childSupport.factors.map((factor, index) => (
                    <li key={index}>• {factor}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-secondary-900 mb-3">Calculation Method</h4>
                <p className="text-secondary-800 mb-3">{stateInfo.childSupport.calculation}</p>
                <p className="text-sm text-secondary-700">
                  <strong>Modifications:</strong> {stateInfo.childSupport.modifications}
                </p>
              </div>
            </div>
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-accent-900 mb-3">Enforcement in {stateInfo.name}</h4>
              <p className="text-accent-800">{stateInfo.childSupport.enforcement}</p>
            </div>
          </div>
        );

      case 'resources':
        return (
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900">Resources Available in {stateInfo.name}</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white border border-primary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiScale} className="mr-2 text-primary-600" />
                  Court Resources
                </h4>
                <ul className="space-y-3">
                  {stateInfo.resources.courts.map((resource, index) => (
                    <ResourceLink key={index} resource={resource} />
                  ))}
                </ul>
              </div>

              <div className="bg-white border border-secondary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiUsers} className="mr-2 text-secondary-600" />
                  Support Services
                </h4>
                <ul className="space-y-3">
                  {stateInfo.resources.support.map((service, index) => (
                    <ResourceLink key={index} resource={service} />
                  ))}
                </ul>
              </div>

              <div className="bg-white border border-accent-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiFileText} className="mr-2 text-accent-600" />
                  Legal Assistance
                </h4>
                <ul className="space-y-3">
                  {stateInfo.resources.legal.map((legal, index) => (
                    <ResourceLink key={index} resource={legal} />
                  ))}
                </ul>
              </div>

              <div className="bg-white border border-primary-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <SafeIcon icon={FiBookOpen} className="mr-2 text-primary-600" />
                  Educational Resources
                </h4>
                <ul className="space-y-3">
                  {stateInfo.resources.educational.map((edu, index) => (
                    <ResourceLink key={index} resource={edu} />
                  ))}
                </ul>
              </div>
            </div>

            {/* Emergency Contacts for Nebraska */}
            {state === 'nebraska' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-red-900 mb-4">Emergency Contacts</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-red-800">Domestic Violence Hotline</h5>
                    <p className="text-red-700">1-800-876-6238 (24/7)</p>
                  </div>
                  <div>
                    <h5 className="font-medium text-red-800">Child Abuse Hotline</h5>
                    <p className="text-red-700">1-800-652-1999</p>
                  </div>
                  <div>
                    <h5 className="font-medium text-red-800">Legal Aid Emergency</h5>
                    <p className="text-red-700">1-877-250-2016</p>
                  </div>
                  <div>
                    <h5 className="font-medium text-red-800">Crisis Text Line</h5>
                    <p className="text-red-700">Text HOME to 741741</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary-50 to-secondary-50">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Link
            to="/states"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to States
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Family Law Resources for {stateInfo.name}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Comprehensive information about family law, custody, child support, and legal requirements specific to {stateInfo.name}, with a focus on protecting fathers' rights.
          </p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="border-b border-primary-200 mb-8"
        >
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-primary-300'
                }`}
              >
                <SafeIcon icon={tab.icon} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-lg border border-primary-200 shadow-sm p-8"
        >
          {getTabContent()}
        </motion.div>
      </div>
    </div>
  );
};

export default StateDetail;